import { Checkbox, Form, Select } from 'antd'
import { useMemo } from 'react'

import { useClusterResources } from '@/hooks/useClusterResources'
import { useClusters } from '@/hooks/useClusters'

import type { PriceResourceType } from './AddPriceModal'
import { formatVmConfig } from './priceColumns'

interface Props {
  resourceType: PriceResourceType
  clusterId?: string
}

export function ResourceFields({ resourceType, clusterId }: Props) {
  const clusters = useClusters()
  const { configurations, availableStorage } = useClusterResources(clusterId)

  const storageTypes = useMemo(() => {
    const types = new Set<string>()
    for (const list of Object.values(availableStorage)) {
      for (const s of list) {
        types.add(s.storageType)
      }
    }
    return Array.from(types)
  }, [availableStorage])

  return (
    <>
      <Form.Item name="clusterId" label="Cluster" rules={[{ required: true }]}>
        <Select placeholder="Select cluster">
          {clusters.map((c) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {resourceType === 'vm' && (
        <Form.Item
          name="vmConfigurationId"
          label="VM Configuration"
          rules={[{ required: true }]}
        >
          <Select
            placeholder={
              clusterId ? 'Select configuration' : 'Select a cluster first'
            }
            disabled={!clusterId}
          >
            {configurations.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {formatVmConfig(c)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {resourceType === 'storage' && (
        <>
          <Form.Item
            name="storageType"
            label="Storage Type"
            rules={[{ required: true }]}
          >
            <Select
              placeholder={clusterId ? 'Select type' : 'Select a cluster first'}
              disabled={!clusterId}
            >
              {(storageTypes.length > 0
                ? storageTypes
                : ['HDD', 'SSD', 'NVME']
              ).map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="replicated" valuePropName="checked">
            <Checkbox>Replicated</Checkbox>
          </Form.Item>
        </>
      )}
      {resourceType === 'public-ip' && (
        <Form.Item
          name="addressType"
          label="Address Type"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select type">
            <Select.Option value="V4">IPv4</Select.Option>
            <Select.Option value="V6">IPv6</Select.Option>
          </Select>
        </Form.Item>
      )}
    </>
  )
}
